import {Injectable, inject} from "@angular/core";
import {HttpErrorResponse} from "@angular/common/http";
import {ComponentStore} from "@ngrx/component-store";
import {EMPTY, Observable} from "rxjs";
import {catchError, exhaustMap, switchMap, tap} from "rxjs/operators";
import {ThreePLServiceDetails} from "../models/threepl-service-details.model";
import {ThreePLServicesListQuery} from "../models/threepl-services-list.model";
import {ThreePLServiceHttpService, ThreePLServicesPage} from "../services/threepl-service-http.service";
import {applyServicePush} from "../services/threepl-services-channel.service";

export interface ThreePLServicesState {
  query: ThreePLServicesListQuery | undefined;
  items: ThreePLServiceDetails[];
  cursor: string;
  finished: boolean;
  loading: boolean;
  error: HttpErrorResponse | undefined;
}

const initialState: ThreePLServicesState = {
  query: undefined, items: [], cursor: "", finished: false, loading: false, error: undefined,
};

function withoutListed(incoming: ThreePLServiceDetails[], listed: ThreePLServiceDetails[]): ThreePLServiceDetails[] {
  const seen = new Set(listed.map((item) => item.id));
  return incoming.filter((service) => !seen.has(service.id) && !!seen.add(service.id));
}

@Injectable()
export class ThreePLServicesStore extends ComponentStore<ThreePLServicesState> {
  private readonly http = inject(ThreePLServiceHttpService);

  readonly vm$ = this.select((s) => ({
    items: s.items,
    loading: s.loading,
    empty: !s.loading && !s.items.length,
  }));

  constructor() {
    super(initialState);
  }

  private readonly started = this.updater((state, query: ThreePLServicesListQuery): ThreePLServicesState => ({
    ...initialState, query, loading: true,
  }));

  private readonly pageLoaded = this.updater((state, page: ThreePLServicesPage): ThreePLServicesState => ({
    ...state,
    items: [...state.items, ...withoutListed(page.list, state.items)],
    cursor: page.cursor,
    finished: !page.cursor || page.list.length < state.query.limit,
    loading: false,
  }));

  private readonly failed = this.updater((state, error: HttpErrorResponse): ThreePLServicesState => ({
    ...state, loading: false, error,
  }));

  readonly prepend = this.updater((state, services: ThreePLServiceDetails[]): ThreePLServicesState => {
    const added = withoutListed(services, state.items);
    return added.length ? {...state, items: [...added, ...state.items]} : state;
  });

  readonly applyPush = this.updater((state, push: {action: "changed" | "partialChanged"; entities: Partial<ThreePLServiceDetails>[]}) => {
    let touched = false;
    const items = state.items.map((item) => {
      const pushed = push.entities.find((entity) => entity?.id === item.id);
      if (!pushed) {
        return item;
      }
      const next = applyServicePush(item, push.action, pushed);
      touched = touched || next !== item;
      return next;
    });
    return touched ? {...state, items} : state;
  });

  readonly load = this.effect((query$: Observable<ThreePLServicesListQuery>) =>
    query$.pipe(
      tap((query) => this.started(query)),
      switchMap((query) => this.fetch(query, undefined)),
    ),
  );

  readonly loadMore = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      exhaustMap(() => {
        const {query, cursor, finished, loading} = this.get();
        if (!query || finished || loading) {
          return EMPTY;
        }
        this.patchState({loading: true});
        return this.fetch(query, cursor);
      }),
    ),
  );

  private fetch(query: ThreePLServicesListQuery, cursor: string | undefined): Observable<ThreePLServicesPage> {
    return this.http.list(query, cursor).pipe(
      tap({
        next: (page) => this.get().query === query && this.pageLoaded(page),
        error: (error: HttpErrorResponse) => this.get().query === query && this.failed(error),
      }),
      catchError(() => EMPTY),
    );
  }
}
